import type { Locale, TranslationTree } from "@llmgateway/i18n";

export const codeMessages: Partial<Record<Locale, TranslationTree>> = {
	en: {
		brand: {
			byGateway: "by doteb",
		},
		common: {
			copyCode: "Copy code",
		},
		nav: {
			models: "Models",
			guides: "Guides",
			pricing: "Pricing",
			compare: "Compare",
			docs: "Docs",
			dashboard: "Dashboard",
			signIn: "Sign in",
			getStarted: "Get Started",
			openMenu: "Open menu",
			closeMenu: "Close menu",
		},
		guides: {
			searchPlaceholder: "Search guides...",
			searchAriaLabel: "Search guides",
			noResultsTitle: 'No guides match "{query}".',
			noResultsDescription:
				"Try a different keyword, or clear the search to see all integrations.",
			comingSoon: "Coming soon",
			planModeOnly: "Plan mode only",
			descriptions: {
				autohand:
					"Use doteb with Autohand Code for autonomous AI-powered coding in your terminal, IDE, and Slack.",
				claudeCode:
					"Use doteb with Claude Code for AI-powered terminal assistance and coding.",
				cursor:
					"Use doteb with Cursor IDE in plan mode only. Cursor's coding agent does not work with external API endpoints.",
				codexCli:
					"Use doteb with OpenAI's Codex CLI for AI-powered terminal coding.",
				cline:
					"Use doteb with Cline for AI-powered coding assistance in VS Code.",
				continueCli:
					"Use doteb with Continue's open-source AI code assistant CLI.",
				hermesAgent:
					"Use doteb with Nous Research's Hermes Agent for terminal-based AI coding.",
				kiloCode:
					"Use doteb with Kilo Code in VS Code for autonomous AI coding with built-in provider support.",
				kimiCode:
					"Use doteb with Kimi Code CLI for autonomous terminal-based AI coding.",
				n8n: "Connect n8n workflow automation to doteb for AI-powered workflows.",
				opencode:
					"Use doteb with OpenCode CLI for AI-powered development workflows.",
				opencodeDesktop:
					"Use doteb with OpenCode Desktop app - connect via GUI, no config files needed.",
				openclaw:
					"Use doteb with OpenClaw for AI-powered chat across supported chat apps.",
				pi: "Use doteb with Pi coding agent for AI-powered terminal coding with any model.",
				vscode:
					"Native VS Code integration for AI-powered code completion and chat.",
			},
		},
		auth: {
			email: "Email",
			password: "Password",
			nameOptional: "Name (optional)",
			newPassword: "New password",
			confirmPassword: "Confirm password",
			divider: "Or",
			signIn: "Sign in",
			signUp: "Sign up",
			continueWithPasskey: "Sign in with passkey",
			socialError: "Failed to sign in with {provider}",
			validation: {
				email: "Please enter a valid email address",
				passwordLength: "Password must be at least 8 characters",
				passwordRequired: "Password is required",
				passwordsMatch: "Passwords don't match",
			},
			login: {
				title: "Sign in",
				subtitle: "Sign in to access your Dev Plan",
				heroTitle: "Welcome back,\ndeveloper.",
				heroSubtitle:
					"Your dev environment is ready. Pick up where you left off.",
				terminalStatus: "devpass status",
				terminalTasks: "3 active plans, 12 tasks completed today",
				terminalOk: "All systems operational.",
				forgotPassword: "Forgot password?",
				submitting: "Signing in...",
				success: "Login successful",
				unknownError: "An unknown error occurred",
				passkeyError: "Failed to sign in with passkey",
				noAccount: "Don't have an account? Sign up",
			},
			signup: {
				title: "Create an account",
				subtitle: "Get started with your dev tools",
				subtitleWithPlan: "Sign up to get started with the {plan} plan",
				heroTitle: "Ship faster\nwith AI.",
				heroSubtitle:
					"Dev plans, coding tools, and AI-powered workflows for modern development teams.",
				setupLine: "Setting up project structure...",
				generatingLine: "Generating dev plan...",
				readyLine: "Ready! Run `devpass start` to begin.",
				submitting: "Creating account...",
				submit: "Create account",
				success: "Account created",
				successDescription:
					"Please check your email to verify your account before signing in.",
				error: "Failed to sign up",
				hasAccount: "Already have an account? Sign in",
			},
			forgot: {
				heroTitle: "Forgot it?\nWe've got you.",
				heroSubtitle:
					"A quick reset and you're back to shipping. Check your inbox for the link.",
				title: "Forgot your password?",
				subtitle: "Enter your email and we'll send you a link to reset it.",
				submitting: "Sending reset link...",
				submit: "Send reset link",
				checkEmail: "Check your email",
				checkEmailDescription:
					"If an account exists for {email}, we've sent a password reset link. The link expires in 1 hour.",
				tryAgainPrefix: "Didn't get it? Check your spam folder, or",
				tryAgain: "try again",
				tryAgainSuffix: ".",
				backToSignIn: "Back to sign in",
				error: "Failed to send reset email",
			},
			reset: {
				heroTitle: "Set a new\npassword.",
				heroSubtitle:
					"Pick something you'll remember - but ideally something a password manager picks for you.",
				title: "Set a new password",
				subtitle: "Choose a strong password - at least 8 characters.",
				invalidTitle: "Reset link invalid or expired",
				invalidDescription:
					"This password reset link is no longer valid. Request a new one to continue.",
				requestNew: "Request a new link",
				missingToken: "Missing reset token. Request a new reset link.",
				submitting: "Updating password...",
				submit: "Update password",
				success: "Password updated",
				successDescription: "Sign in with your new password.",
				error: "Failed to reset password",
			},
			featureCli: "CLI Tools",
			featureAiCoding: "AI Coding",
			featureSmartPlans: "Smart Plans",
		},
		home: {
			heroBadge: "Built for Claude Code · OpenCode · SoulForge",
			heroLine1: "One key. Every model.",
			heroLine2: "Three flat prices.",
			heroPrefix: "DevPass turns every dollar you spend into",
			heroMiddle: "of model usage at provider rates. Pair it with",
			heroSuffix: "the graph-powered agent — and that stretches to roughly",
			heroToolLine:
				"Works the same in Claude Code, OpenCode, SoulForge, Cline, and every OpenAI-compatible tool — no SDK changes.",
			seePricing: "See pricing",
			toolsEyebrow: "Native fit",
			toolsTitle: "Drop-in for the agents you already use",
			toolsDescription:
				"DevPass is built around how Claude Code, OpenCode, and SoulForge actually work — not a generic OpenAI-compatible proxy you have to glue together.",
			toolsMore:
				"+ Cline, Cursor, Aider, Continue & any OpenAI-compatible tool",
			pricingEyebrow: "Pricing",
			pricingTitle: "What you pay vs. what you get",
			pricingDescription:
				"Every plan includes the full 200+ model catalog. The only thing that changes is the size of your monthly usage allowance.",
			comparePricing: "Compare every feature on the pricing page",
			howItWorksTitle: "Up and running in minutes",
			modelsEyebrow: "The latest flagships",
			modelsTitle: "Every plan ships with the newest models",
			modelsDescription:
				"Claude Opus 4.7, Gemini 3.1 Pro, GPT-5.5 Pro, plus the strongest open-weight Chinese coders — included on every tier.",
			finalTitle: "Stop watching your token balance",
			finalDescription: "Pick a plan, set two env vars, get back to building.",
			browseModels: "Browse all models",
			featuredTools: {
				claudeCode:
					"Two env vars and Claude Code routes through doteb. Use any model — Claude, GPT-5, Gemini, GLM — with a single ANTHROPIC_MODEL flip.",
				opencode:
					"doteb is built into OpenCode. Run `opencode`, type `/connect`, paste your DevPass key. No env vars, no config files.",
				soulforge:
					"Graph-powered coding agent. Maps your repo on launch and edits TypeScript by symbol name, not by find-and-replace. Roughly half the tokens of an equivalent Claude Code or OpenCode session. Pair with DevPass to double your monthly usage.",
				soulforgeHighlight: "Graph-powered · −50%",
			},
			steps: {
				pickPlanTitle: "Pick a plan",
				pickPlanDescription:
					"Choose Lite, Pro, or Max. Your DevPass key works everywhere — no separate keys per tool.",
				plugInTitle: "Plug it into your agent",
				plugInDescription:
					"Two env vars for Claude Code, /providers in OpenCode, /keys in SoulForge. No SDK changes, no code refactor.",
				switchModelsTitle: "Switch models freely",
				switchModelsDescription:
					"Claude Opus 4.7 for architecture, GPT-5.5 for review, Gemini 3.1 Pro for fresh eyes — same key, no extra cost.",
			},
			terminal: {
				worksWith: "Works with",
				anyCompatibleTool: "+ any OpenAI-compatible tool",
				comments: {
					worksAnyModel: "# works with any model — switch freely",
					opencode: "# doteb is built-in — type /connect to link your key",
					soulforge:
						"# inside: /keys → paste your DevPass key (or: soulforge --set-key llmgateway <key>)",
				},
			},
		},
		faq: {
			eyebrow: "FAQ",
			title: "Common questions",
			description:
				"Everything you need to know about usage limits, plans, and getting started with DevPass.",
			contactPrefix: "Can't find an answer?",
			contactUs: "Contact us",
			items: {
				usage: {
					question: "How much usage do I get on each plan?",
					answer:
						"Every plan includes a fixed monthly usage allowance, calculated at standard provider rates. Lite ({litePrice}/mo) includes {liteUsage} in model usage, Pro ({proPrice}/mo) includes {proUsage}, and Max ({maxPrice}/mo) includes {maxUsage} — roughly 3× the subscription price.",
				},
				calculation: {
					question: "How is usage calculated?",
					answer:
						"Usage is metered at each provider's published per-token rate (input, output, and cached tokens). You can see the dollar value of every request in your dashboard in real time.",
				},
				limit: {
					question: "What happens if I hit my monthly limit?",
					answer:
						"Requests pause until your allowance resets at the start of the next billing cycle. You can upgrade to a higher tier at any time for an immediate, prorated boost to your available usage.",
				},
				changePlans: {
					question: "Can I change plans anytime?",
					answer:
						"Yes. Upgrade or downgrade whenever you like — changes are prorated and take effect immediately. There's no lock-in and no cancellation fee.",
				},
				tools: {
					question: "Which tools and SDKs work with DevPass?",
					answer:
						"Anything that speaks the OpenAI or Anthropic API — Claude Code, SoulForge, Cursor, Cline, Continue, Aider, the OpenAI and Anthropic SDKs, and more. Set two environment variables and you're in.",
				},
				models: {
					question: "Are all 200+ models included on every plan?",
					answer:
						"Yes. Every plan includes the full catalog — Claude, GPT-5, Gemini, Llama, Qwen, and the rest. Plans differ only in the size of your monthly usage allowance.",
				},
			},
		},
		footer: {
			newsletter: {
				successTitle: "You're in!",
				successDescription:
					"Check your inbox — we'll send you the good stuff, no filler.",
				eyebrow: "Newsletter",
				title: "Stay ahead of the curve",
				description:
					"Join developers who get weekly insights on new models, cost optimization, and AI coding tips — straight to their inbox.",
				submitting: "Subscribing...",
				submit: "Subscribe — it's free",
				noSpam: "No spam. Unsubscribe anytime.",
				error: "Something went wrong. Please try again.",
			},
			sections: {
				product: "Product",
				resources: "Resources",
				community: "Community",
			},
			links: {
				codingModels: "Coding Models",
				pricing: "Pricing",
				comparePlans: "Compare Plans",
				allModels: "All Models",
				documentation: "Documentation",
				integrations: "Integrations",
				blog: "Blog",
				privacyPolicy: "Privacy Policy",
				termsOfUse: "Terms of Use",
			},
			copyright: "All rights reserved.",
		},
		pricing: {
			pageEyebrow: "Pricing & plans",
			heroLine1: "Flat rate. Every model.",
			heroLine2: "No token math.",
			heroPrefix: "Every dollar you pay turns into",
			heroMiddle: "of model usage at provider rates - and roughly",
			heroSuffix:
				"when you pair DevPass with SoulForge, the graph-powered agent that treats your code as structure, not strings.",
			popular: "Most popular",
			perMonth: "/mo",
			valueLabel: "{ratio}x value",
			whatYouGet: "What you actually get",
			youPay: "You pay",
			youUse: "You use",
			paid: "{amount} paid",
			used: "{amount} used",
			providerRates: "at provider rates",
			withSoulForge:
				"With SoulForge -> graph-powered context cuts roughly half the tokens, stretching your {amount} to ~{effective} of effective use.",
			getPlan: "Get {plan}",
			usageMetered:
				"Usage is metered at each provider's published per-token rate. Every request shows its dollar value in your dashboard in real time.",
			invoiceInfo:
				"Need company/address details on your invoice? Update billing settings before purchase. We email the invoice automatically after payment.",
			fairUse:
				"Premium-tier frontier models (Anthropic Opus, OpenAI Pro/reasoning, Gemini Pro, Grok 4) are subject to a weekly fair-use allowance ($10 / $50 / $140 for Lite / Pro / Max) in addition to the monthly credit allowance. All other 190+ models use the full monthly allowance with the 3x multiplier.",
			comparePlans: "Compare plans",
			whatsIncluded: "What's in each plan",
			whatsIncludedDescription:
				"Every tier ships with the full model catalog. The only thing that changes is how much usage you have to play with each month.",
			feature: "Feature",
			included: "Included",
			notIncluded: "Not included",
			usageFootnote:
				"Usage is metered at each provider's published per-token rate (input, output, and cached tokens). Every request shows its dollar value in your dashboard in real time. SoulForge savings vary by workload - ~50% is typical when the agent works through a real project over multiple turns.",
			calculatorTitle: "Not sure which plan fits?",
			calculatorDescription:
				"Estimate your monthly cost with the token calculator - pick a model, paste a sample prompt, and see what your usage actually looks like.",
			openCalculator: "Open calculator",
			stillDeciding: "Still deciding?",
			bottomCta:
				"Start on Pro - most developers ship from there. Switch tiers any time, prorated.",
			talkToUs: "Talk to us",
			goToDashboard: "Go to Dashboard",
			getYourDevPass: "Get your DevPass",
			plans: {
				lite: {
					name: "Lite",
					description: "For occasional AI-assisted coding",
					tagline: "Casual hobby work",
					feature1: "All 200+ models - Claude, GPT-5, Gemini, GLM, Qwen, ...",
					feature2:
						"Works with Claude Code, OpenCode, SoulForge & every OpenAI-compatible tool",
					feature3: "Real-time usage dashboard with per-request cost",
					feature4: "Switch tiers any time - prorated",
				},
				pro: {
					name: "Pro",
					description: "For daily development workflows",
					tagline: "Most developers ship from here",
					feature1: "Everything in Lite",
					feature2:
						"Headroom for full-day agent runs in Claude Code & OpenCode",
					feature3: "Priority routing on flagship models",
					feature4: "Email support with 1-business-day reply",
				},
				max: {
					name: "Max",
					description: "For power users and heavy sessions",
					tagline: "All-day agent runs",
					feature1: "Everything in Pro",
					feature2: "Comfortable for non-stop SoulForge & Claude Code usage",
					feature3: "Priority support, faster turnaround",
					feature4: "Best $/usage ratio across the lineup",
				},
			},
			rows: {
				youPay: "You pay",
				monthlyUsage: "Monthly model usage at provider rates",
				effectiveSoulForge: "Effective with SoulForge (~50% fewer tokens)",
				modelsIncluded: "Models included",
				latestFlagships: "Latest flagships (Opus 4.7, GPT-5.5, Gemini 3.1 Pro)",
				chineseCoders: "Open-weight Chinese coders (GLM-4.7, Qwen3, Kimi K2.6)",
				worksWith: "Works with Claude Code, OpenCode, SoulForge",
				compatibleTools: "Any OpenAI/Anthropic-compatible tool",
				usageDashboard: "Real-time usage dashboard",
				costAnalytics: "Per-request cost & latency analytics",
				switchTiers: "Switch tiers anytime (prorated)",
				emailSupport: "Email support",
				prioritySupport: "Priority support",
				agentRuns: "Headroom for all-day agent runs",
			},
		},
		codingModels: {
			pageTitle: "AI Models for Coding",
			heroDescription:
				"High-performance models optimized for coding tasks. All models support tool calling, JSON output, streaming, and prompt caching.",
			viewPricing: "View Pricing",
			toolCalling: "Tool Calling",
			toolCallingDescription:
				"Execute code, run commands, and interact with external APIs.",
			jsonOutput: "JSON Output",
			jsonOutputDescription:
				"Structured responses for seamless integration with your tools.",
			streaming: "Streaming",
			streamingDescription:
				"Real-time responses for faster feedback during development.",
			promptCaching: "Prompt Caching",
			promptCachingDescription:
				"Reduce costs and latency with intelligent prompt caching.",
			featuredTitle: "Featured Coding Models",
			featuredDescription:
				"We default to cheap frontier open-weight models - flagship is one tab away when you need it.",
			ctaTitle: "Start coding with AI today",
			ctaDescription:
				"Get your DevPass and access all coding models with a single API key.",
			showcaseTitle: "Recommended for coding agents",
			filterAria: "Filter coding models",
			cheap: "Cheap",
			flagship: "Flagship",
			all: "All",
			cheapDescription: "Frontier open-weight models - best price per token.",
			flagshipDescription:
				"Top-tier closed models - highest capability per call.",
			allDescription:
				"Every recommended coding model, with cheap open-weights listed first.",
			defaultDescription:
				"High-performance models optimized for coding tasks with tool support and prompt caching.",
			copyModelId: "Copy model ID",
			context: "Context:",
			input: "in",
			output: "out",
			perMillionTokens: "/M tokens",
			viewAll: "View all coding models",
		},
		compare: {
			eyebrow: "Comparisons",
			indexTitle: "DevPass vs the alternatives",
			indexDescription:
				"Single-vendor plans and single-model deals each do one thing well. DevPass gives you all 200+ models - frontier and open-weight - under one key. Here's how it stacks up.",
			vs: "vs",
			readComparison: "Read the comparison",
			allComparisons: "All comparisons",
			comparison: "Comparison",
			getDevPass: "Get your DevPass",
			seeAllPlans: "See all plans",
			shortVersion: "The short version",
			atGlance: "DevPass vs {competitor} at a glance",
			pricingAsOf:
				"{tagline}. Pricing and limits as of {date} - always confirm current details on each provider's site.",
			faq: "Frequently asked questions",
			oneKey: "One key. Every model.",
			bottomCta:
				"Start on Pro - most developers ship from there. Switch tiers any time, prorated.",
			compareOtherPlans: "Compare other plans",
			feature: "Feature",
			yes: "Yes",
			no: "No",
		},
		emailVerification: {
			errorTitle: "Error",
			sendError: "Failed to send verification email",
			sentTitle: "Verification email sent",
			sentDescription: "Please check your inbox for the verification email.",
			title: "Your email is unverified.",
			description:
				"Please check your inbox and click the verification link to access all features.",
			sending: "Sending...",
			resend: "Resend email",
		},
		dashboard: {
			time: {
				minutes: "{count} min",
				hours: "{count} hr",
				days: "{count} d",
				months: "{count} mo",
			},
			nav: {
				usage: "Usage",
				billing: "Billing",
				profile: "Profile",
				settings: "Settings",
				signOut: "Sign out",
			},
			activation: {
				loadingStripe: {
					title: "Preparing payment",
					description: "Loading secure payment confirmation.",
				},
				finalizing: {
					title: "Activating DevPass",
					description:
						"Creating your subscription and checking payment status.",
				},
				authenticating: {
					title: "Confirming payment",
					description: "Complete the secure authentication prompt to continue.",
				},
				processing: {
					title: "Payment is processing",
					description:
						"DevPass will activate as soon as Stripe confirms the payment.",
				},
				success: {
					title: "DevPass activated",
					description: "Refreshing your dashboard.",
				},
				error: {
					title: "Activation failed",
					description: "Refresh this page to retry DevPass activation.",
				},
				stripeNotReady: "Stripe is not ready. Please refresh and try again.",
				authenticationFailed: "Payment authentication failed",
				activatedToast: "DevPass activated",
				processingToast:
					"Payment is processing. DevPass will activate shortly.",
				failedToast: "Failed to activate DevPass",
				startSubscriptionFailed: "Failed to start subscription",
				duplicateCardTitle: "Card already in use",
				duplicateCardMessage:
					"This card is already associated with another DevPass account. Please use a different payment method.",
				duplicateCardDescription:
					"This card is already associated with another DevPass account.",
				duplicateCardAction:
					"You were not charged. Please try again with a different payment method.",
				gotIt: "Got it",
			},
			inactivePlan: {
				title: "Choose your Dev Plan",
				description:
					"Pick a plan to get your API key and start coding with 200+ models. Every dollar gives you 3x in usage.",
			},
			plans: {
				popular: "Popular",
				perMonth: "/mo",
				inUsage: "in usage",
				modelUsage: "{amount} model usage",
				allModels: "All 200+ models",
				resetsMonthly: "Resets monthly",
				subscribe: "Subscribe",
				invoiceInfo:
					"Need company/address details on your invoice? Update billing settings before purchase. We email the invoice automatically after payment.",
				descriptions: {
					lite: "For occasional coding",
					pro: "For daily development",
					max: "For power users",
				},
			},
			apiKey: {
				title: "API Key",
				pending: "API key will appear here after setup",
				copied: "Copied to clipboard",
				hide: "Hide",
				reveal: "Reveal",
				copy: "Copy",
				rotateKey: "Rotate key",
				rotateConfirmTitle:
					"Click again to confirm - this invalidates the current key",
				rotateConfirmHelp:
					"Click rotate again to confirm. The current key will stop working immediately.",
				setupGuides: "Setup guides",
				allModels: "All models",
				rotated: "API key rotated",
				rotatedDescription:
					"Update your tools with the new key. The previous key no longer works.",
				rotateError: "Failed to rotate API key",
			},
			quickStart: {
				title: "Quick start",
				copy: "Copy",
				copied: "Copied",
				snippetCopied: "Snippet copied",
				comments: {
					worksAnyModel: "# works with any model - switch freely",
					opencode: "# doteb is built-in - type /connect to link your key",
					soulforge:
						"# inside: /keys -> paste your DevPass key (or: soulforge --set-key llmgateway <key>)",
				},
			},
			activity: {
				title: "Coding activity",
				summary:
					"{requests} requests across {days} active days in the last year",
				streak: "{count}-day streak",
				loading: "Loading your activity...",
				mon: "Mon",
				wed: "Wed",
				fri: "Fri",
				less: "Less",
				more: "More",
				request: "request",
				requests: "requests",
			},
			usage: {
				planLabel: "{plan} plan",
				pricePerMonth: "${price}/mo",
				since: "Since {date}",
				active: "Active",
				renewsIn: "Renews in {duration}",
				cancelsAtPeriodEnd: "Cancels at period end",
				periodCycle: "this cycle",
				period30d: "30d",
				remainingOf: "of ${limit} remaining",
				usedPercent: "{percent}% used",
				thisPeriod: "${amount} this period",
				lowWarning:
					"Above 80% of your monthly allowance. Consider upgrading or wait for the next reset.",
				exhaustedWarning:
					"Allowance reached for this billing cycle. Upgrade to keep coding.",
				spendLabel: "Spend {period}",
				requestsLabel: "Requests {period}",
				tokensLabel: "Tokens {period}",
				allowanceHint: "of ${amount} allowance",
				peakDay: "Peak day",
				codingModels: "Coding models",
			},
			modelChart: {
				title: "Model Usage Overview",
				subtitle:
					"Stacked model {metric} across your DevPass project over {range}",
				metricAria: "Metric",
				loading: "Loading...",
				noActivity: "No activity in this range.",
				moreModels: "+{count} more",
				metrics: {
					requests: "Requests",
					cost: "Cost",
					tokens: "Tokens",
					requestsInline: "requests",
					costInline: "cost",
					tokensInline: "tokens",
				},
				ranges: {
					last4Hours: "last 4 hours",
					last24Hours: "last 24 hours",
					last7Days: "last 7 days",
					last30Days: "last 30 days",
				},
			},
			codingAgents: {
				title: "Coding Agents",
				description:
					"Per-tool usage, costs, and recent activity from the last 30 days.",
				activeCount: "{count} active",
				requestCount: "{count} requests",
				requests: "Requests",
				tokens: "Tokens",
				lastActive: "Last active",
				emptyTitle: "No agent activity yet",
				emptyDescription:
					"This view will fill in once you use your DevPass key with any of the supported coding agents. Set two env vars and run your tool.",
				errorHint:
					"Couldn't reach the activity service just now - refresh in a moment if this looks wrong.",
				justNow: "Just now",
				minutesAgo: "{count}m ago",
				hoursAgo: "{count}h ago",
				daysAgo: "{count}d ago",
			},
			agentDetail: {
				backToDashboard: "Back to dashboard",
				loadErrorTitle: "Couldn't load usage",
				loadErrorDescription:
					"Something went wrong fetching {agent} data. Refresh the page to try again.",
				noActiveTitle: "No active DevPass",
				noActiveDescription:
					"Subscribe to DevPass to see usage details for {agent}.",
				noActivityTitle: "No activity for {agent}",
				noActivityDescription:
					"We haven't seen any requests from this agent in the last 30 days. Run it with your DevPass key and it will show up here.",
				modelUsage: "Model usage",
				noModelUsage: "No model usage data.",
				model: "Model",
				provider: "Provider",
				requests: "Requests",
				tokens: "Tokens",
				cost: "Cost",
				cacheCost: "Cache cost",
				usage: "Usage",
				recentRequests: "Recent requests",
				noRequests: "No requests yet.",
				cacheStatus: "Cache status",
				cached: "Cached",
				partiallyCached: "Partially cached",
				notCached: "Not cached",
			},
			integrations: {
				title: "Integrations",
				seeMoreGuides: "See more guides",
				descriptions: {
					opencode: "AI dev workflows",
					claudeCode: "Terminal AI assistant",
					soulforge: "Soul AI coding agent",
					kiloCode: "VS Code autonomous agent",
				},
			},
			billing: {
				title: "Billing",
				description: "Manage your DevPass subscription and plan.",
				defaultPlanName: "DevPass",
				planLabel: "{plan} plan",
				cycles: {
					monthly: "monthly",
					annual: "annual",
				},
				cancelling: "Cancelling",
				monthlyUsage: "${price}/mo · ${usage} in monthly usage",
				ends: "Ends {date}",
				renews: "Renews {date}",
				renewsRelative: "Renews {date} (in {duration})",
				resume: "Resume subscription",
				cancel: "Cancel subscription",
				cancelTitle: "Cancel your Dev Plan?",
				cancelDescription:
					"Your plan stays active until the end of the current billing period. You won't be charged again, and you can resume any time before then.",
				keep: "Keep subscription",
				cancelledToast: "Subscription cancelled",
				cancelledDescription:
					"Your plan will remain active until the end of your billing period.",
				cancelError: "Failed to cancel subscription",
				resumedToast: "Subscription resumed",
				resumeError: "Failed to resume subscription",
				planUpdatedToast: "Plan updated",
				changeError: "Failed to change plan",
				info: "Your DevPass subscription is billed separately from doteb pay-as-you-go credits, so it won't appear in the standard doteb billing dashboard. Manage the plan and payment method for DevPass right here.",
				infoAlternativeProvider:
					"Your DevPass subscription is billed through Alipay. Cancellation and resume are available here, but payment method management and self-service plan changes are not.",
			},
			paymentMethod: {
				title: "Payment method",
				description: "The card used for your DevPass subscription.",
				updateCard: "Update card",
				addCard: "Add card",
				expires: "Expires {month}/{year}",
				none: "No card on file for this subscription.",
				unavailable:
					"Card-based DevPass billing is unavailable in this deployment.",
				loadingForm: "Loading payment form...",
				confirmError: "Failed to confirm card",
				updatedToast: "Payment method updated",
				updateError: "Failed to update card",
				cancel: "Cancel",
				saveCard: "Save card",
			},
			planChange: {
				title: "Change plan",
				description:
					"Upgrades take effect immediately; downgrades apply at your next renewal.",
				currentPlanFallback: "your plan",
				current: "Current",
				upgrade: "Upgrade",
				downgrade: "Downgrade",
				usageLine: "${amount} in usage",
				switchTo: "Switch to {plan}",
				upgradeTitle: "Upgrade to {plan}?",
				switchTitle: "Switch to {plan}?",
				upgradeDescription:
					"You'll be charged a prorated amount today for the rest of your current billing period, then ${price}/mo going forward. Your usage allowance increases to ${usage} right away.",
				downgradeDescription:
					"You'll keep your {currentPlan} allowance until your next renewal, when you'll move to {plan} (${price}/mo, ${usage} in usage). No refund is issued for the current period.",
				invoiceInfo:
					"Need company/address details on your invoice? Update billing settings before confirming. We email the invoice automatically after payment.",
				keep: "Keep {plan}",
				confirmUpgrade: "Upgrade to {plan}",
				confirmSwitch: "Switch to {plan}",
			},
			settings: {
				title: "Settings",
				description: "Control how DevPass routes and stores your requests.",
				account: "Account",
				email: "Email",
				sectionTitle: "Settings",
				updateError: "Failed to update settings",
				routingUpdated: "Routing strategy updated",
				routingError: "Failed to update routing strategy",
				retentionEnabled: "Data retention enabled",
				retentionMetadata: "Switched to metadata-only",
				retentionError: "Failed to update data retention",
				routing: {
					label: "Default routing strategy",
					description:
						"How the gateway picks a provider when a model is served by more than one. Throughput and latency strategies aren't available on coding plans because they bypass prompt-cache-aware routing.",
					learnMore: "Learn more",
					options: {
						auto: "Automatic (recommended)",
						price: "Cheapest",
						throughput: "Highest throughput",
						latency: "Lowest latency",
					},
				},
				retention: {
					label: "Retain request data",
					description:
						"Store full request and response payloads for analytics and debugging. When off, only metadata is kept. Storage is billed, and this is only required when using the Responses API or for debugging purposes.",
				},
				advanced: "Advanced",
				allowAllModels: {
					label: "Allow all models",
					description: "Enable access beyond the curated coding model list",
					enabledToast: "All models enabled",
					restrictedToast: "Restricted to coding models",
					warningTitle: "Prompt caching may not be available.",
					warningDescription:
						"Coding models are selected because they support prompt caching, which reduces costs and latency. Non-curated models may cost more.",
					optimized:
						"Using coding-optimized models with prompt caching, tool calling, JSON output, and streaming.",
				},
			},
			deleteAccount: {
				title: "Delete Account",
				description1:
					"This action is irreversible. Your account and personal data, including login credentials and personal API keys, will be permanently deleted.",
				description2Prefix:
					"Billing records of credits you purchased and spent are retained for 10 years as required by tax and accounting law. See our",
				privacyPolicy: "Privacy Policy",
				description2Suffix: "for details.",
				button: "Delete Account",
				deleting: "Deleting...",
				success: "Your account has been successfully deleted.",
				error: "An error occurred",
				dialogTitle: "Are you absolutely sure?",
				dialogDescription:
					"This permanently deletes your account and personal data, including login credentials and personal API keys. This action cannot be undone. Billing records of credits you purchased and spent are retained for 10 years as required by tax and accounting law.",
				cancel: "Cancel",
			},
			feedback: {
				validationReason: "Please pick a reason.",
				validationComments: "Keep it under 2000 characters.",
				successToast: "Thanks for the feedback!",
				errorToast: "Couldn't save your feedback. Please try again.",
				titleUpdate: "Update your feedback",
				titleNew: "Sorry to see you go - what happened?",
				description:
					"Your Dev Plan was cancelled{plan}. A few seconds of feedback will directly shape what we build next.",
				primaryReason: "Primary reason",
				commentsLabel: "Anything else? (optional)",
				commentsPlaceholder:
					"What got in the way, what was missing, or what would bring you back?",
				skip: "Skip",
				saving: "Saving...",
				updateButton: "Update feedback",
				sendButton: "Send feedback",
				noFeedbackTitle: "No feedback to share right now",
				noFeedbackDescription:
					"This page is for sharing feedback after cancelling a Dev Plan. We don't see a recent cancellation on your account.",
				backToDashboard: "Back to dashboard",
				reasons: {
					tooExpensive: {
						label: "Too expensive",
						description: "The price didn't fit my budget.",
					},
					missingFeatures: {
						label: "Missing features",
						description: "Something I needed wasn't available.",
					},
					notUsingEnough: {
						label: "Not using it enough",
						description: "I didn't get enough value out of it.",
					},
					switchedAlternative: {
						label: "Switched to an alternative",
						description: "I'm using something else now.",
					},
					other: {
						label: "Something else",
						description: "I'll explain in the comments.",
					},
				},
			},
		},
		profile: {
			title: "Your profile",
			description:
				"Share your AI coding activity. Profiles are private by default.",
			publicProfile: "Public profile",
			publicDescription: "Anyone with your link can view this profile.",
			privateDescription: "Only you can see this profile.",
			username: "Username",
			usernamePlaceholder: "your-handle",
			usernameHelp:
				"3-30 characters, lowercase letters, numbers, hyphens or underscores.",
			displayName: "Display name",
			githubUsername: "GitHub username",
			githubPlaceholder: "octocat",
			xUsername: "X username",
			xPlaceholder: "handle",
			bio: "Bio",
			bioPlaceholder: "A short line about what you build.",
			saveProfile: "Save profile",
			saving: "Saving...",
			profileSaved: "Profile saved",
			saveError: "Failed to save profile",
			linkCopied: "Link copied",
			copy: "Copy",
			view: "View",
			preview: "Preview",
			defaultUser: "DevPass user",
			joinedToday: "Joined today",
			joinedDays: "Joined {count} days ago",
			joinedDay: "Joined 1 day ago",
			joinedMonths: "Joined {count} months ago",
			joinedMonth: "Joined 1 month ago",
			joinedYears: "Joined {count} years ago",
			joinedYear: "Joined 1 year ago",
			tokens: "Tokens",
			requests: "Requests",
			currentStreak: "Current streak",
			longestStreak: "Longest streak",
			daySuffix: "d",
			codingActivity: "Coding activity",
			activeDays: "{count} active days in the last year",
			codingAgents: "Coding agents",
			models: "Models",
			providers: "Providers",
			tokensOverTime: "Tokens over time",
			last30Days: "Last 30 days",
			heatmap: {
				mon: "Mon",
				wed: "Wed",
				fri: "Fri",
				less: "Less",
				more: "More",
				request: "request",
				requests: "requests",
			},
			noTokenUsage: "No token usage in the last 30 days yet.",
			tokensTooltip: "{tokens} tokens",
		},
	},
	"zh-CN": {
		brand: {
			byGateway: "来自 doteb",
		},
		common: {
			copyCode: "复制代码",
		},
		nav: {
			models: "模型",
			guides: "指南",
			pricing: "价格",
			compare: "对比",
			docs: "文档",
			dashboard: "控制台",
			signIn: "登录",
			getStarted: "开始使用",
			openMenu: "打开菜单",
			closeMenu: "关闭菜单",
		},
		guides: {
			searchPlaceholder: "搜索指南...",
			searchAriaLabel: "搜索指南",
			noResultsTitle: '没有匹配 "{query}" 的指南。',
			noResultsDescription: "请尝试其他关键词，或清空搜索查看全部集成。",
			comingSoon: "即将推出",
			planModeOnly: "仅计划模式",
			descriptions: {
				autohand:
					"将 doteb 与 Autohand Code 配合使用，在终端、IDE 和 Slack 中获得自主 AI 编码能力。",
				claudeCode:
					"将 doteb 与 Claude Code 配合使用，获得 AI 驱动的终端辅助和编码能力。",
				cursor:
					"将 doteb 与 Cursor IDE 配合使用，仅支持计划模式。Cursor 的编码 agent 不支持外部 API endpoint。",
				codexCli:
					"将 doteb 与 OpenAI 的 Codex CLI 配合使用，获得 AI 驱动的终端编码能力。",
				cline: "将 doteb 与 Cline 配合使用，在 VS Code 中获得 AI 编码辅助。",
				continueCli: "将 doteb 与 Continue 的开源 AI 代码助手 CLI 配合使用。",
				hermesAgent:
					"将 doteb 与 Nous Research 的 Hermes Agent 配合使用，获得基于终端的 AI 编码能力。",
				kiloCode:
					"将 doteb 与 VS Code 中的 Kilo Code 配合使用，通过内置 provider 支持实现自主 AI 编码。",
				kimiCode:
					"将 doteb 与 Kimi Code CLI 配合使用，获得自主终端 AI 编码能力。",
				n8n: "将 n8n 工作流自动化连接到 doteb，构建 AI 驱动的工作流。",
				opencode: "将 doteb 与 OpenCode CLI 配合使用，支持 AI 驱动的开发流程。",
				opencodeDesktop:
					"将 doteb 与 OpenCode Desktop 应用配合使用，通过图形界面连接，无需配置文件。",
				openclaw:
					"将 doteb 与 OpenClaw 配合使用，在支持的聊天应用中进行 AI 聊天。",
				pi: "将 doteb 与 Pi coding agent 配合使用，用任意模型完成 AI 终端编码。",
				vscode: "原生 VS Code 集成，支持 AI 驱动的代码补全和聊天。",
			},
		},
		auth: {
			email: "邮箱",
			password: "密码",
			nameOptional: "姓名（可选）",
			newPassword: "新密码",
			confirmPassword: "确认密码",
			divider: "或",
			signIn: "登录",
			signUp: "注册",
			continueWithPasskey: "使用通行密钥登录",
			socialError: "使用 {provider} 登录失败",
			validation: {
				email: "请输入有效的邮箱地址",
				passwordLength: "密码至少需要 8 个字符",
				passwordRequired: "请输入密码",
				passwordsMatch: "两次输入的密码不一致",
			},
			login: {
				title: "登录",
				subtitle: "登录以访问你的 Dev Plan",
				heroTitle: "欢迎回来，\n开发者。",
				heroSubtitle: "你的开发环境已就绪，可以继续上次的工作。",
				terminalStatus: "devpass status",
				terminalTasks: "3 个计划运行中，今日已完成 12 个任务",
				terminalOk: "所有系统运行正常。",
				forgotPassword: "忘记密码？",
				submitting: "正在登录...",
				success: "登录成功",
				unknownError: "发生未知错误",
				passkeyError: "通行密钥登录失败",
				noAccount: "还没有账号？注册",
			},
			signup: {
				title: "创建账号",
				subtitle: "开始使用你的开发工具",
				subtitleWithPlan: "注册并开始使用 {plan} 套餐",
				heroTitle: "用 AI\n更快交付。",
				heroSubtitle: "面向现代开发团队的开发计划、编码工具和 AI 工作流。",
				setupLine: "正在设置项目结构...",
				generatingLine: "正在生成开发计划...",
				readyLine: "就绪！运行 `devpass start` 开始。",
				submitting: "正在创建账号...",
				submit: "创建账号",
				success: "账号已创建",
				successDescription: "请检查邮箱并验证账号，然后再登录。",
				error: "注册失败",
				hasAccount: "已有账号？登录",
			},
			forgot: {
				heroTitle: "忘记密码？\n我们来帮你。",
				heroSubtitle: "快速重置后即可继续开发。请在收件箱中查看链接。",
				title: "忘记密码？",
				subtitle: "输入邮箱，我们会发送重置链接。",
				submitting: "正在发送重置链接...",
				submit: "发送重置链接",
				checkEmail: "请检查邮箱",
				checkEmailDescription:
					"如果 {email} 对应的账号存在，我们已经发送了密码重置链接。链接将在 1 小时后过期。",
				tryAgainPrefix: "没有收到？请检查垃圾邮件，或",
				tryAgain: "重试",
				tryAgainSuffix: "。",
				backToSignIn: "返回登录",
				error: "发送重置邮件失败",
			},
			reset: {
				heroTitle: "设置\n新密码。",
				heroSubtitle: "请选择一个强密码，最好交给密码管理器生成。",
				title: "设置新密码",
				subtitle: "请选择强密码，至少 8 个字符。",
				invalidTitle: "重置链接无效或已过期",
				invalidDescription: "此密码重置链接已不可用。请重新申请链接后继续。",
				requestNew: "申请新链接",
				missingToken: "缺少重置令牌。请重新申请重置链接。",
				submitting: "正在更新密码...",
				submit: "更新密码",
				success: "密码已更新",
				successDescription: "请使用新密码登录。",
				error: "重置密码失败",
			},
			featureCli: "CLI 工具",
			featureAiCoding: "AI 编程",
			featureSmartPlans: "智能套餐",
		},
		home: {
			heroBadge: "为 Claude Code · OpenCode · SoulForge 打造",
			heroLine1: "一个 key。所有模型。",
			heroLine2: "三档固定价格。",
			heroPrefix: "DevPass 会把你支付的每 1 美元转化为",
			heroMiddle: "的模型用量（按供应商价格计）。搭配",
			heroSuffix: "这个图结构驱动 agent 后，大约可延展到",
			heroToolLine:
				"在 Claude Code、OpenCode、SoulForge、Cline 和所有 OpenAI 兼容工具中用法一致，无需修改 SDK。",
			seePricing: "查看价格",
			toolsEyebrow: "原生适配",
			toolsTitle: "直接接入你已经在用的 agent",
			toolsDescription:
				"DevPass 按 Claude Code、OpenCode 和 SoulForge 的真实使用方式设计，而不是需要你额外拼装的通用 OpenAI 兼容代理。",
			toolsMore: "+ Cline、Cursor、Aider、Continue 和任意 OpenAI 兼容工具",
			pricingEyebrow: "价格",
			pricingTitle: "你支付多少，实际获得多少",
			pricingDescription:
				"每个套餐都包含完整的 200+ 模型目录。唯一变化的是每月可使用的额度大小。",
			comparePricing: "在价格页对比全部能力",
			howItWorksTitle: "几分钟内即可跑起来",
			modelsEyebrow: "最新旗舰模型",
			modelsTitle: "每个套餐都包含最新模型",
			modelsDescription:
				"Claude Opus 4.7、Gemini 3.1 Pro、GPT-5.5 Pro，以及最强的开源权重中文编码模型，每一档都包含。",
			finalTitle: "别再盯着 token 余额",
			finalDescription: "选择套餐，设置两个环境变量，然后继续构建。",
			browseModels: "浏览全部模型",
			featuredTools: {
				claudeCode:
					"两个环境变量即可让 Claude Code 走 doteb。Claude、GPT-5、Gemini、GLM 等模型都能用，只需切换 ANTHROPIC_MODEL。",
				opencode:
					"doteb 已内置在 OpenCode 中。运行 `opencode`，输入 `/connect`，粘贴 DevPass key。无需环境变量，也无需配置文件。",
				soulforge:
					"图结构驱动的编码 agent。启动时映射仓库，并按 TypeScript 符号名编辑，而不是查找替换。相比等价的 Claude Code 或 OpenCode 会话，大约少用一半 token。搭配 DevPass 可让月度用量翻倍。",
				soulforgeHighlight: "图结构驱动 · −50%",
			},
			steps: {
				pickPlanTitle: "选择套餐",
				pickPlanDescription:
					"选择 Lite、Pro 或 Max。你的 DevPass key 可在所有工具中使用，无需给每个工具单独配 key。",
				plugInTitle: "接入你的 agent",
				plugInDescription:
					"Claude Code 设置两个环境变量，OpenCode 使用 /providers，SoulForge 使用 /keys。无需修改 SDK，也无需重构代码。",
				switchModelsTitle: "自由切换模型",
				switchModelsDescription:
					"Claude Opus 4.7 做架构，GPT-5.5 做审查，Gemini 3.1 Pro 提供新视角，同一个 key，无额外成本。",
			},
			terminal: {
				worksWith: "适配工具",
				anyCompatibleTool: "+ 任意 OpenAI 兼容工具",
				comments: {
					worksAnyModel: "# 适用于任意模型，可自由切换",
					opencode: "# doteb 已内置，输入 /connect 关联你的 key",
					soulforge:
						"# 在 /keys 中粘贴你的 DevPass key（或运行：soulforge --set-key llmgateway <key>）",
				},
			},
		},
		faq: {
			eyebrow: "FAQ",
			title: "常见问题",
			description:
				"关于用量限制、套餐和开始使用 DevPass，你需要了解的内容都在这里。",
			contactPrefix: "没有找到答案？",
			contactUs: "联系我们",
			items: {
				usage: {
					question: "每个套餐包含多少用量？",
					answer:
						"每个套餐都包含固定的月度用量额度，并按标准供应商价格计算。Lite（{litePrice}/月）包含 {liteUsage} 模型用量，Pro（{proPrice}/月）包含 {proUsage}，Max（{maxPrice}/月）包含 {maxUsage}，大约是订阅价格的 3 倍。",
				},
				calculation: {
					question: "用量如何计算？",
					answer:
						"用量按各供应商公开的 token 单价计量（输入、输出和缓存 token）。你可以在控制台实时查看每次请求对应的美元价值。",
				},
				limit: {
					question: "如果达到月度额度会怎样？",
					answer:
						"请求会暂停，直到下一个账单周期开始时额度重置。你也可以随时升级到更高档位，立即按比例获得更多可用额度。",
				},
				changePlans: {
					question: "可以随时变更套餐吗？",
					answer:
						"可以。你可以随时升级或降级，变更会按比例计费并立即生效。没有锁定期，也没有取消费用。",
				},
				tools: {
					question: "哪些工具和 SDK 可以配合 DevPass 使用？",
					answer:
						"任何支持 OpenAI 或 Anthropic API 的工具都可以，包括 Claude Code、SoulForge、Cursor、Cline、Continue、Aider、OpenAI 和 Anthropic SDK 等。设置两个环境变量即可使用。",
				},
				models: {
					question: "每个套餐都包含全部 200+ 模型吗？",
					answer:
						"是。每个套餐都包含完整模型目录，包括 Claude、GPT-5、Gemini、Llama、Qwen 等。不同套餐只区别于月度用量额度大小。",
				},
			},
		},
		footer: {
			newsletter: {
				successTitle: "订阅成功！",
				successDescription:
					"请查看收件箱。我们会发送有价值的内容，不发无用信息。",
				eyebrow: "Newsletter",
				title: "持续掌握最新动态",
				description:
					"加入开发者邮件列表，每周获取新模型、成本优化和 AI 编码技巧。",
				submitting: "正在订阅...",
				submit: "免费订阅",
				noSpam: "不发垃圾邮件，可随时退订。",
				error: "发生错误，请重试。",
			},
			sections: {
				product: "产品",
				resources: "资源",
				community: "社区",
			},
			links: {
				codingModels: "编码模型",
				pricing: "价格",
				comparePlans: "套餐对比",
				allModels: "全部模型",
				documentation: "文档",
				integrations: "集成",
				blog: "博客",
				privacyPolicy: "隐私政策",
				termsOfUse: "使用条款",
			},
			copyright: "保留所有权利。",
		},
		pricing: {
			pageEyebrow: "价格与套餐",
			heroLine1: "固定价格。所有模型。",
			heroLine2: "不用再算 token。",
			heroPrefix: "你支付的每 1 美元都会转化为",
			heroMiddle: "按供应商价格计的模型用量；如果搭配 SoulForge，大约可达到",
			heroSuffix:
				"SoulForge 是图结构驱动的 agent，会把代码当作结构而不是字符串处理。",
			popular: "最受欢迎",
			perMonth: "/月",
			valueLabel: "{ratio} 倍价值",
			whatYouGet: "你实际获得",
			youPay: "你支付",
			youUse: "可使用",
			paid: "支付 {amount}",
			used: "使用 {amount}",
			providerRates: "按供应商价格计",
			withSoulForge:
				"搭配 SoulForge -> 图结构上下文可大约减少一半 token，将 {amount} 延展到约 {effective} 的有效用量。",
			getPlan: "购买 {plan}",
			usageMetered:
				"用量按各供应商公开的 token 单价计量。每次请求都会在控制台实时显示美元价值。",
			invoiceInfo:
				"发票需要公司/地址信息？请在购买前更新账单设置。付款后我们会自动发送发票邮件。",
			fairUse:
				"高端旗舰模型（Anthropic Opus、OpenAI Pro/reasoning、Gemini Pro、Grok 4）受每周公平使用额度限制（Lite / Pro / Max 分别为 $10 / $50 / $140），该额度独立于月度信用额度。其余 190+ 模型使用完整月度额度和 3 倍倍率。",
			comparePlans: "套餐对比",
			whatsIncluded: "每个套餐包含什么",
			whatsIncludedDescription:
				"每一档都包含完整模型目录。唯一变化的是每月可使用的额度。",
			feature: "能力",
			included: "包含",
			notIncluded: "不包含",
			usageFootnote:
				"用量按各供应商公开的 token 单价计量（输入、输出和缓存 token）。每次请求都会在控制台实时显示美元价值。SoulForge 的节省效果会随工作负载变化；当 agent 在真实项目中多轮工作时，约 50% 是常见水平。",
			calculatorTitle: "不确定适合哪档？",
			calculatorDescription:
				"用 token 计算器估算月度成本：选择模型、粘贴示例 prompt，然后查看实际用量。",
			openCalculator: "打开计算器",
			stillDeciding: "还在犹豫？",
			bottomCta:
				"从 Pro 开始，很多开发者从这里交付。随时切换套餐，按比例计费。",
			talkToUs: "联系我们",
			goToDashboard: "进入控制台",
			getYourDevPass: "获取 DevPass",
			plans: {
				lite: {
					name: "Lite",
					description: "适合偶尔使用 AI 辅助编码",
					tagline: "轻量个人项目",
					feature1: "全部 200+ 模型 - Claude、GPT-5、Gemini、GLM、Qwen 等",
					feature2:
						"适配 Claude Code、OpenCode、SoulForge 和所有 OpenAI 兼容工具",
					feature3: "实时用量控制台，展示单次请求成本",
					feature4: "随时切换套餐，按比例计费",
				},
				pro: {
					name: "Pro",
					description: "适合日常开发工作流",
					tagline: "多数开发者从这里开始交付",
					feature1: "Lite 的全部能力",
					feature2: "支撑 Claude Code 和 OpenCode 的全天 agent 运行",
					feature3: "旗舰模型优先路由",
					feature4: "邮件支持，1 个工作日内回复",
				},
				max: {
					name: "Max",
					description: "适合重度用户和长会话",
					tagline: "全天 agent 运行",
					feature1: "Pro 的全部能力",
					feature2: "适合持续使用 SoulForge 和 Claude Code",
					feature3: "优先支持，更快响应",
					feature4: "全系列最佳用量价格比",
				},
			},
			rows: {
				youPay: "你支付",
				monthlyUsage: "按供应商价格计的月度模型用量",
				effectiveSoulForge: "搭配 SoulForge 的有效用量（约少 50% token）",
				modelsIncluded: "包含模型数",
				latestFlagships: "最新旗舰模型（Opus 4.7、GPT-5.5、Gemini 3.1 Pro）",
				chineseCoders: "开源权重中文编码模型（GLM-4.7、Qwen3、Kimi K2.6）",
				worksWith: "适配 Claude Code、OpenCode、SoulForge",
				compatibleTools: "任何 OpenAI/Anthropic 兼容工具",
				usageDashboard: "实时用量控制台",
				costAnalytics: "单次请求成本与延迟分析",
				switchTiers: "随时切换套餐（按比例计费）",
				emailSupport: "邮件支持",
				prioritySupport: "优先支持",
				agentRuns: "全天 agent 运行余量",
			},
		},
		codingModels: {
			pageTitle: "面向编码的 AI 模型",
			heroDescription:
				"针对编码任务优化的高性能模型。所有模型都支持工具调用、JSON 输出、流式响应和 prompt 缓存。",
			viewPricing: "查看价格",
			toolCalling: "工具调用",
			toolCallingDescription: "执行代码、运行命令，并与外部 API 交互。",
			jsonOutput: "JSON 输出",
			jsonOutputDescription: "结构化响应，便于无缝接入你的工具。",
			streaming: "流式响应",
			streamingDescription: "实时返回结果，让开发反馈更快。",
			promptCaching: "Prompt 缓存",
			promptCachingDescription: "通过智能 prompt 缓存降低成本和延迟。",
			featuredTitle: "精选编码模型",
			featuredDescription:
				"默认展示低成本前沿开源权重模型；需要旗舰能力时，一键切换标签即可。",
			ctaTitle: "今天就开始用 AI 编码",
			ctaDescription: "获取 DevPass，用一个 API key 访问所有编码模型。",
			showcaseTitle: "推荐给 coding agents",
			filterAria: "筛选编码模型",
			cheap: "低成本",
			flagship: "旗舰",
			all: "全部",
			cheapDescription: "前沿开源权重模型，token 性价比最高。",
			flagshipDescription: "顶级闭源模型，单次调用能力最强。",
			allDescription: "所有推荐编码模型，低成本开源权重模型优先展示。",
			defaultDescription:
				"针对编码任务优化，支持工具调用和 prompt 缓存的高性能模型。",
			copyModelId: "复制模型 ID",
			context: "上下文：",
			input: "输入",
			output: "输出",
			perMillionTokens: "/百万 token",
			viewAll: "查看全部编码模型",
		},
		compare: {
			eyebrow: "对比",
			indexTitle: "DevPass 与替代方案对比",
			indexDescription:
				"单供应商套餐和单模型方案各有优势。DevPass 用一个 key 提供全部 200+ 模型，包括旗舰模型和开源权重模型。下面是具体对比。",
			vs: "对比",
			readComparison: "查看对比",
			allComparisons: "全部对比",
			comparison: "对比",
			getDevPass: "获取 DevPass",
			seeAllPlans: "查看全部套餐",
			shortVersion: "简短结论",
			atGlance: "DevPass 与 {competitor} 快速对比",
			pricingAsOf:
				"{tagline}。价格与限制截至 {date}；请始终到各供应商官网确认最新详情。",
			faq: "常见问题",
			oneKey: "一个 key。所有模型。",
			bottomCta:
				"从 Pro 开始，很多开发者从这里交付。随时切换套餐，按比例计费。",
			compareOtherPlans: "对比其他套餐",
			feature: "能力",
			yes: "是",
			no: "否",
		},
		emailVerification: {
			errorTitle: "错误",
			sendError: "发送验证邮件失败",
			sentTitle: "验证邮件已发送",
			sentDescription: "请检查收件箱中的验证邮件。",
			title: "你的邮箱尚未验证。",
			description: "请检查收件箱并点击验证链接，以访问全部功能。",
			sending: "正在发送...",
			resend: "重新发送邮件",
		},
		dashboard: {
			time: {
				minutes: "{count} 分钟",
				hours: "{count} 小时",
				days: "{count} 天",
				months: "{count} 个月",
			},
			nav: {
				usage: "用量",
				billing: "账单",
				profile: "资料",
				settings: "设置",
				signOut: "退出登录",
			},
			activation: {
				loadingStripe: {
					title: "正在准备付款",
					description: "正在加载安全付款确认。",
				},
				finalizing: {
					title: "正在激活 DevPass",
					description: "正在创建订阅并检查付款状态。",
				},
				authenticating: {
					title: "正在确认付款",
					description: "请完成安全认证提示以继续。",
				},
				processing: {
					title: "付款处理中",
					description: "Stripe 确认付款后，DevPass 会立即激活。",
				},
				success: {
					title: "DevPass 已激活",
					description: "正在刷新控制台。",
				},
				error: {
					title: "激活失败",
					description: "请刷新此页面后重试 DevPass 激活。",
				},
				stripeNotReady: "Stripe 尚未准备好。请刷新后重试。",
				authenticationFailed: "付款认证失败",
				activatedToast: "DevPass 已激活",
				processingToast: "付款处理中，DevPass 将很快激活。",
				failedToast: "DevPass 激活失败",
				startSubscriptionFailed: "启动订阅失败",
				duplicateCardTitle: "此银行卡已被使用",
				duplicateCardMessage:
					"此银行卡已绑定到另一个 DevPass 账号。请使用其他付款方式。",
				duplicateCardDescription: "此银行卡已绑定到另一个 DevPass 账号。",
				duplicateCardAction: "本次未扣款。请使用其他付款方式后重试。",
				gotIt: "知道了",
			},
			inactivePlan: {
				title: "选择你的 Dev Plan",
				description:
					"选择套餐以获取 API key，并开始使用 200+ 模型编码。每 1 美元都可获得 3 倍用量。",
			},
			plans: {
				popular: "热门",
				perMonth: "/月",
				inUsage: "用量",
				modelUsage: "{amount} 模型用量",
				allModels: "全部 200+ 模型",
				resetsMonthly: "每月重置",
				subscribe: "订阅",
				invoiceInfo:
					"发票需要公司/地址信息？请在购买前更新账单设置。付款后我们会自动发送发票邮件。",
				descriptions: {
					lite: "适合偶尔编码",
					pro: "适合日常开发",
					max: "适合重度用户",
				},
			},
			apiKey: {
				title: "API Key",
				pending: "设置完成后，API key 会显示在这里",
				copied: "已复制到剪贴板",
				hide: "隐藏",
				reveal: "显示",
				copy: "复制",
				rotateKey: "轮换 key",
				rotateConfirmTitle: "再次点击确认 - 当前 key 会立即失效",
				rotateConfirmHelp: "再次点击轮换以确认。当前 key 会立即停止工作。",
				setupGuides: "设置指南",
				allModels: "全部模型",
				rotated: "API key 已轮换",
				rotatedDescription:
					"请在工具中更新为新 key。此前的 key 已无法继续使用。",
				rotateError: "轮换 API key 失败",
			},
			quickStart: {
				title: "快速开始",
				copy: "复制",
				copied: "已复制",
				snippetCopied: "代码片段已复制",
				comments: {
					worksAnyModel: "# 适用于任意模型 - 可自由切换",
					opencode: "# doteb 已内置 - 输入 /connect 关联你的 key",
					soulforge:
						"# 在 /keys 中粘贴你的 DevPass key（或运行：soulforge --set-key llmgateway <key>）",
				},
			},
			activity: {
				title: "编码活动",
				summary: "过去一年共 {requests} 次请求，{days} 个活跃日",
				streak: "连续活跃 {count} 天",
				loading: "正在加载你的活动...",
				mon: "周一",
				wed: "周三",
				fri: "周五",
				less: "少",
				more: "多",
				request: "次请求",
				requests: "次请求",
			},
			usage: {
				planLabel: "{plan} 套餐",
				pricePerMonth: "${price}/月",
				since: "自 {date} 起",
				active: "已启用",
				renewsIn: "{duration} 后续订",
				cancelsAtPeriodEnd: "将在周期结束时取消",
				periodCycle: "本周期",
				period30d: "30 天",
				remainingOf: "剩余 ${limit}",
				usedPercent: "已用 {percent}%",
				thisPeriod: "本周期 ${amount}",
				lowWarning: "已超过月度额度的 80%。可考虑升级，或等待下次重置。",
				exhaustedWarning: "本账单周期额度已用完。升级后可继续编码。",
				spendLabel: "{period}花费",
				requestsLabel: "{period}请求数",
				tokensLabel: "{period} Tokens",
				allowanceHint: "共 ${amount} 额度",
				peakDay: "峰值日",
				codingModels: "编码模型",
			},
			modelChart: {
				title: "模型用量概览",
				subtitle: "{range}内，DevPass 项目的模型{metric}堆叠图",
				metricAria: "指标",
				loading: "加载中...",
				noActivity: "此时间范围内暂无活动。",
				moreModels: "另有 {count} 个",
				metrics: {
					requests: "请求数",
					cost: "成本",
					tokens: "Tokens",
					requestsInline: "请求数",
					costInline: "成本",
					tokensInline: "Tokens",
				},
				ranges: {
					last4Hours: "过去 4 小时",
					last24Hours: "过去 24 小时",
					last7Days: "过去 7 天",
					last30Days: "过去 30 天",
				},
			},
			codingAgents: {
				title: "Coding Agents",
				description: "过去 30 天各工具的用量、成本和最近活动。",
				activeCount: "{count} 个活跃",
				requestCount: "{count} 次请求",
				requests: "请求数",
				tokens: "Tokens",
				lastActive: "最近活跃",
				emptyTitle: "暂无 agent 活动",
				emptyDescription:
					"当你使用 DevPass key 连接任何受支持的 coding agent 后，这里会显示数据。设置两个环境变量并运行你的工具即可。",
				errorHint: "刚才无法连接活动服务；如果数据看起来不对，请稍后刷新。",
				justNow: "刚刚",
				minutesAgo: "{count} 分钟前",
				hoursAgo: "{count} 小时前",
				daysAgo: "{count} 天前",
			},
			agentDetail: {
				backToDashboard: "返回控制台",
				loadErrorTitle: "无法加载用量",
				loadErrorDescription: "获取 {agent} 数据时出错。请刷新页面重试。",
				noActiveTitle: "没有启用的 DevPass",
				noActiveDescription: "订阅 DevPass 后即可查看 {agent} 的用量详情。",
				noActivityTitle: "{agent} 暂无活动",
				noActivityDescription:
					"过去 30 天我们还没有看到此 agent 的请求。使用你的 DevPass key 运行它后，数据会显示在这里。",
				modelUsage: "模型用量",
				noModelUsage: "暂无模型用量数据。",
				model: "模型",
				provider: "供应商",
				requests: "请求数",
				tokens: "Tokens",
				cost: "成本",
				cacheCost: "缓存成本",
				usage: "用量",
				recentRequests: "最近请求",
				noRequests: "暂无请求。",
				cacheStatus: "缓存状态",
				cached: "已缓存",
				partiallyCached: "部分缓存",
				notCached: "未缓存",
			},
			integrations: {
				title: "集成",
				seeMoreGuides: "查看更多指南",
				descriptions: {
					opencode: "AI 开发工作流",
					claudeCode: "终端 AI 助手",
					soulforge: "Soul AI 编码 agent",
					kiloCode: "VS Code 自主 agent",
				},
			},
			billing: {
				title: "账单",
				description: "管理你的 DevPass 订阅和套餐。",
				defaultPlanName: "DevPass",
				planLabel: "{plan} 套餐",
				cycles: {
					monthly: "月付",
					annual: "年付",
				},
				cancelling: "取消中",
				monthlyUsage: "${price}/月 · 每月 ${usage} 用量",
				ends: "{date} 结束",
				renews: "{date} 续订",
				renewsRelative: "{date} 续订（{duration} 后）",
				resume: "恢复订阅",
				cancel: "取消订阅",
				cancelTitle: "取消你的 Dev Plan？",
				cancelDescription:
					"套餐会保持有效至当前账单周期结束。之后不会再次扣款，在结束前你可以随时恢复订阅。",
				keep: "保留订阅",
				cancelledToast: "订阅已取消",
				cancelledDescription: "套餐会保持有效至当前账单周期结束。",
				cancelError: "取消订阅失败",
				resumedToast: "订阅已恢复",
				resumeError: "恢复订阅失败",
				planUpdatedToast: "套餐已更新",
				changeError: "变更套餐失败",
				info: "DevPass 订阅与 doteb 按量付费额度分开计费，因此不会出现在标准 doteb 账单控制台中。你可以在这里管理 DevPass 套餐和付款方式。",
				infoAlternativeProvider:
					"你的 DevPass 订阅通过支付宝计费。你可以在这里取消或恢复订阅，但暂不支持自助管理付款方式或自助切换套餐。",
			},
			paymentMethod: {
				title: "付款方式",
				description: "用于 DevPass 订阅的银行卡。",
				updateCard: "更新银行卡",
				addCard: "添加银行卡",
				expires: "{month}/{year} 到期",
				none: "此订阅暂无已保存银行卡。",
				unavailable: "当前部署未启用基于银行卡的 DevPass 计费。",
				loadingForm: "正在加载付款表单...",
				confirmError: "确认银行卡失败",
				updatedToast: "付款方式已更新",
				updateError: "更新银行卡失败",
				cancel: "取消",
				saveCard: "保存银行卡",
			},
			planChange: {
				title: "变更套餐",
				description: "升级会立即生效；降级将在下次续订时生效。",
				currentPlanFallback: "当前套餐",
				current: "当前",
				upgrade: "升级",
				downgrade: "降级",
				usageLine: "${amount} 用量",
				switchTo: "切换到 {plan}",
				upgradeTitle: "升级到 {plan}？",
				switchTitle: "切换到 {plan}？",
				upgradeDescription:
					"今天会按比例收取当前账单周期剩余时间的费用，之后为 ${price}/月。你的用量额度会立即提升到 ${usage}。",
				downgradeDescription:
					"你会继续保留 {currentPlan} 额度直到下次续订，届时会切换到 {plan}（${price}/月，${usage} 用量）。当前周期不退款。",
				invoiceInfo:
					"发票需要公司/地址信息？确认前请更新账单设置。付款后我们会自动发送发票邮件。",
				keep: "保留 {plan}",
				confirmUpgrade: "升级到 {plan}",
				confirmSwitch: "切换到 {plan}",
			},
			settings: {
				title: "设置",
				description: "控制 DevPass 如何路由和存储你的请求。",
				account: "账号",
				email: "邮箱",
				sectionTitle: "设置",
				updateError: "更新设置失败",
				routingUpdated: "路由策略已更新",
				routingError: "更新路由策略失败",
				retentionEnabled: "已启用数据保留",
				retentionMetadata: "已切换为仅保留元数据",
				retentionError: "更新数据保留设置失败",
				routing: {
					label: "默认路由策略",
					description:
						"当一个模型由多个供应商提供时，网关会按此策略选择供应商。编码套餐暂不支持吞吐量和延迟策略，因为它们会绕过感知 prompt 缓存的路由。",
					learnMore: "了解更多",
					options: {
						auto: "自动（推荐）",
						price: "最低价格",
						throughput: "最高吞吐量",
						latency: "最低延迟",
					},
				},
				retention: {
					label: "保留请求数据",
					description:
						"保存完整请求和响应内容，用于分析和调试。关闭后只保留元数据。存储会产生费用，通常只在使用 Responses API 或调试时需要。",
				},
				advanced: "高级",
				allowAllModels: {
					label: "允许全部模型",
					description: "启用精选编码模型列表之外的访问",
					enabledToast: "已启用全部模型",
					restrictedToast: "已限制为编码模型",
					warningTitle: "Prompt 缓存可能不可用。",
					warningDescription:
						"编码模型因支持 prompt 缓存而被精选，可降低成本和延迟。非精选模型可能成本更高。",
					optimized:
						"正在使用针对编码优化的模型，支持 prompt 缓存、工具调用、JSON 输出和流式响应。",
				},
			},
			deleteAccount: {
				title: "删除账号",
				description1:
					"此操作不可逆。你的账号和个人数据，包括登录凭据和个人 API key，都会被永久删除。",
				description2Prefix:
					"根据税务和会计法律要求，你购买和使用额度的账单记录会保留 10 年。详情请查看我们的",
				privacyPolicy: "隐私政策",
				description2Suffix: "。",
				button: "删除账号",
				deleting: "正在删除...",
				success: "你的账号已成功删除。",
				error: "发生错误",
				dialogTitle: "你确定要继续吗？",
				dialogDescription:
					"这会永久删除你的账号和个人数据，包括登录凭据和个人 API key。此操作无法撤销。根据税务和会计法律要求，你购买和使用额度的账单记录会保留 10 年。",
				cancel: "取消",
			},
			feedback: {
				validationReason: "请选择一个原因。",
				validationComments: "请控制在 2000 个字符以内。",
				successToast: "感谢你的反馈！",
				errorToast: "无法保存反馈，请重试。",
				titleUpdate: "更新你的反馈",
				titleNew: "很遗憾你要离开 - 发生了什么？",
				description:
					"你的 Dev Plan 已取消{plan}。花几秒反馈一下，会直接影响我们接下来构建的内容。",
				primaryReason: "主要原因",
				commentsLabel: "还有其他想说的吗？（可选）",
				commentsPlaceholder: "是什么阻碍了你、缺少了什么，或什么会让你回来？",
				skip: "跳过",
				saving: "正在保存...",
				updateButton: "更新反馈",
				sendButton: "发送反馈",
				noFeedbackTitle: "现在没有可提交的反馈",
				noFeedbackDescription:
					"此页面用于在取消 Dev Plan 后分享反馈。我们没有在你的账号中看到最近的取消记录。",
				backToDashboard: "返回控制台",
				reasons: {
					tooExpensive: {
						label: "太贵",
						description: "价格不符合我的预算。",
					},
					missingFeatures: {
						label: "缺少功能",
						description: "我需要的某些能力还不可用。",
					},
					notUsingEnough: {
						label: "使用不够多",
						description: "我没有获得足够价值。",
					},
					switchedAlternative: {
						label: "切换到了替代方案",
						description: "我现在在使用其他工具。",
					},
					other: {
						label: "其他原因",
						description: "我会在评论里说明。",
					},
				},
			},
		},
		profile: {
			title: "你的资料",
			description: "分享你的 AI 编码活动。资料默认保持私密。",
			publicProfile: "公开资料",
			publicDescription: "任何拥有链接的人都可以查看此资料。",
			privateDescription: "只有你可以查看此资料。",
			username: "用户名",
			usernamePlaceholder: "your-handle",
			usernameHelp: "3-30 个字符，可使用小写字母、数字、连字符或下划线。",
			displayName: "显示名称",
			githubUsername: "GitHub 用户名",
			githubPlaceholder: "octocat",
			xUsername: "X 用户名",
			xPlaceholder: "handle",
			bio: "简介",
			bioPlaceholder: "简单介绍你正在构建什么。",
			saveProfile: "保存资料",
			saving: "正在保存...",
			profileSaved: "资料已保存",
			saveError: "保存资料失败",
			linkCopied: "链接已复制",
			copy: "复制",
			view: "查看",
			preview: "预览",
			defaultUser: "DevPass 用户",
			joinedToday: "今天加入",
			joinedDays: "{count} 天前加入",
			joinedDay: "1 天前加入",
			joinedMonths: "{count} 个月前加入",
			joinedMonth: "1 个月前加入",
			joinedYears: "{count} 年前加入",
			joinedYear: "1 年前加入",
			tokens: "Tokens",
			requests: "请求数",
			currentStreak: "当前连续天数",
			longestStreak: "最长连续天数",
			daySuffix: "天",
			codingActivity: "编码活动",
			activeDays: "过去一年有 {count} 个活跃日",
			codingAgents: "Coding agents",
			models: "模型",
			providers: "供应商",
			tokensOverTime: "Tokens 趋势",
			last30Days: "最近 30 天",
			heatmap: {
				mon: "周一",
				wed: "周三",
				fri: "周五",
				less: "少",
				more: "多",
				request: "次请求",
				requests: "次请求",
			},
			noTokenUsage: "最近 30 天还没有 token 用量。",
			tokensTooltip: "{tokens} tokens",
		},
	},
};
